import { Box, Paper, Typography } from '@mui/material';
import { useMemo, useState, type MouseEvent } from 'react';

export interface FunnelStage {
  label: string;
  value: number;
}

export interface SalesFunnelProps {
  data: FunnelStage[];
  title?: string;
}

const COLOR_START = '#EE3750';
const COLOR_END_EXTENDED = '#FDF0F1';
const TOOLTIP_BG = '#FDF0F1';
const TOOLTIP_TEXT = '#EE3750';

const PLOT_WIDTH = 380;
const PADDING = 5;
const LABEL_GAP = 8;
const MIN_SEGMENT_HEIGHT = 70;

const parseHex = (hex: string): [number, number, number] => {
  const normalized = hex.replace('#', '');
  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
};

const mixHex = (from: string, to: string, ratio: number): string => {
  const [fr, fg, fb] = parseHex(from);
  const [tr, tg, tb] = parseHex(to);
  const channel = (start: number, end: number) =>
    Math.round(start + (end - start) * ratio);

  return `#${[channel(fr, tr), channel(fg, tg), channel(fb, tb)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`;
};

const getStageColors = (count: number): string[] => {
  if (count <= 0) return [];
  if (count === 1) return [COLOR_START];

  return Array.from({ length: count }, (_, index) =>
    mixHex(COLOR_START, COLOR_END_EXTENDED, index / (count - 1))
  );
};

interface FunnelSegmentLayout {
  label: string;
  value: number;
  color: string;
  path: string;
  valueX: number;
  valueY: number;
  labelX: number;
  labelY: number;
}

const buildFunnelLayout = (
  stages: FunnelStage[],
  colors: string[]
): { segments: FunnelSegmentLayout[]; chartHeight: number; svgWidth: number } => {
  const stageCount = stages.length;
  if (!stageCount) {
    return { segments: [], chartHeight: 260, svgWidth: 520 };
  }

  const centerX = PADDING + PLOT_WIDTH / 2;
  const firstValue = Math.max(stages[0].value, 1);
  const segmentHeight = Math.max(
    MIN_SEGMENT_HEIGHT,
    (260 - PADDING * 2) / stageCount
  );
  const chartHeight = PADDING * 2 + segmentHeight * stageCount;

  const widthForValue = (value: number) =>
    (Math.max(value, 0) / firstValue) * PLOT_WIDTH;

  let maxLabelX = centerX;

  const segments = stages.map((stage, index) => {
    const yStart = PADDING + index * segmentHeight;
    const yEnd = PADDING + (index + 1) * segmentHeight;
    const centerY = yStart + segmentHeight / 2;
    const topWidth = widthForValue(stage.value);
    const bottomWidth =
      index < stageCount - 1
        ? widthForValue(stages[index + 1].value)
        : 0;

    const xTopLeft = centerX - topWidth / 2;
    const xTopRight = centerX + topWidth / 2;
    const xBottomLeft = centerX - bottomWidth / 2;
    const xBottomRight = centerX + bottomWidth / 2;

    const path = `M ${xTopLeft},${yStart} L ${xTopRight},${yStart} L ${xBottomRight},${yEnd} L ${xBottomLeft},${yEnd} Z`;

    // Right edge of this segment at its vertical center
    const rightEdgeX = centerX + (topWidth + bottomWidth) / 4;
    const labelX = rightEdgeX + LABEL_GAP;

    maxLabelX = Math.max(maxLabelX, labelX + stage.label.length * 6.5);

    return {
      label: stage.label,
      value: stage.value,
      color: colors[index],
      path,
      valueX: centerX,
      valueY: centerY,
      labelX,
      labelY: centerY,
    };
  });

  const svgWidth = Math.max(maxLabelX + PADDING, PLOT_WIDTH + PADDING * 2);

  return { segments, chartHeight, svgWidth };
};

const buildSubtitle = (stages: FunnelStage[]) =>
  stages.map((stage) => stage.label).join(' → ');

export const SalesFunnel = ({
  data,
  title = 'Sales Funnel',
}: SalesFunnelProps) => {
  const stages = data ?? [];
  const stageCount = stages.length;
  const colors = useMemo(() => getStageColors(stageCount), [stageCount]);

  const { segments, chartHeight, svgWidth } = useMemo(
    () => buildFunnelLayout(stages, colors),
    [stages, colors]
  );

  const subtitle = buildSubtitle(stages);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const hoveredSegment =
    hoveredIndex != null ? segments[hoveredIndex] : null;

  const handleHover = (index: number, event: MouseEvent<SVGElement>) => {
    const container = event.currentTarget.ownerSVGElement?.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setHoveredIndex(index);
    setTooltipPos({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const clearHover = () => setHoveredIndex(null);

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        bgcolor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '16px',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
        p: '24px',
        fontFamily: 'Inter, Poppins, sans-serif',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box>
        <Typography
          sx={{
            fontFamily: 'inherit',
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            fontWeight: 700,
            color: '#1A1C2E',
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontFamily: 'inherit',
              fontSize: '0.9375rem',
              color: '#6B7280',
              mt: 0.75,
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 2, sm: 3 },
          width: '100%',
        }}
      >
        {!stageCount ? (
          <Typography sx={{ color: '#6B7280', fontSize: '0.9375rem' }}>
            No funnel data available
          </Typography>
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxHeight: 320,
            }}
          >
            <Box
              component='svg'
              role='application'
              viewBox={`0 0 ${svgWidth} ${chartHeight}`}
              preserveAspectRatio='xMidYMid meet'
              aria-label='Sales funnel chart'
              sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                maxHeight: 320,
              }}
            >
              <g>
                {segments.map((segment, index) => (
                  <path
                    key={`path-${segment.label}-${index}`}
                    d={segment.path}
                    fill={segment.color}
                    stroke='#FFFFFF'
                    strokeWidth={2}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => handleHover(index, e)}
                    onMouseMove={(e) => handleHover(index, e)}
                    onMouseLeave={clearHover}
                  />
                ))}
              </g>

              <g>
                {segments.map((segment, index) => (
                  <text
                    key={`value-${segment.label}-${index}`}
                    x={segment.valueX}
                    y={segment.valueY}
                    textAnchor='middle'
                    dominantBaseline='middle'
                    fill='#FFFFFF'
                    fontSize='13'
                    fontWeight='600'
                    fontFamily='Inter, Poppins, sans-serif'
                    style={{ pointerEvents: 'none' }}
                  >
                    {segment.value.toLocaleString()}
                  </text>
                ))}
              </g>

              <g>
                {segments.map((segment, index) => (
                  <text
                    key={`label-${segment.label}-${index}`}
                    x={segment.labelX}
                    y={segment.labelY}
                    textAnchor='start'
                    dominantBaseline='middle'
                    fill={
                      hoveredIndex === index ? '#EE3750' : '#334155'
                    }
                    fontSize='12'
                    fontWeight={hoveredIndex === index ? '600' : '500'}
                    fontFamily='Inter, Poppins, sans-serif'
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => handleHover(index, e)}
                    onMouseMove={(e) => handleHover(index, e)}
                    onMouseLeave={clearHover}
                  >
                    {segment.label}
                  </text>
                ))}
              </g>
            </Box>

            {hoveredSegment && (
              <Box
                sx={{
                  position: 'absolute',
                  left: tooltipPos.x,
                  top: tooltipPos.y,
                  transform: 'translate(-50%, calc(-100% - 10px))',
                  bgcolor: TOOLTIP_BG,
                  color: TOOLTIP_TEXT,
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.75,
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.16)',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  zIndex: 2,
                  fontSize: '0.8125rem',
                  lineHeight: 1.4,
                  border: '1px solid rgba(140, 98, 64, 0.2)',
                }}
              >
                {hoveredSegment.label}:{' '}
                <Box
                  component='span'
                  sx={{ fontWeight: 700 }}
                >
                  {hoveredSegment.value.toLocaleString()}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};
